import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { Activity, Clock, Zap, TrendingUp, Calendar, Target } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const GeneralFitnessAnalytics: React.FC = () => {
  const { user } = useAuth();

  const { data: fitnessData, isLoading } = useQuery({
    queryKey: ['general-fitness-analytics', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('general_fitness')
        .select('*')
        .eq('user_id', user.id)
        .order('recorded_date', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const stats = React.useMemo(() => {
    if (!fitnessData?.length) return null;

    const totalActivities = fitnessData.length;
    const totalDuration = fitnessData.reduce((sum, activity) => sum + (activity.duration_seconds || 0), 0);
    const totalCalories = fitnessData.reduce((sum, activity) => sum + (activity.calories_burned || 0), 0);
    
    // Activity type distribution
    const activityCounts = fitnessData.reduce((acc, activity) => {
      acc[activity.activity_type] = (acc[activity.activity_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const activityDistribution = Object.entries(activityCounts).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count,
      percentage: Math.round((count / totalActivities) * 100)
    }));

    // Weekly activity volume
    const weeklyData = fitnessData.reduce((acc, activity) => {
      const date = new Date(activity.recorded_date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!acc[weekKey]) {
        acc[weekKey] = { week: weekKey, activities: 0, duration: 0, calories: 0 };
      }
      
      acc[weekKey].activities++;
      acc[weekKey].duration += activity.duration_seconds || 0;
      acc[weekKey].calories += activity.calories_burned || 0;
      
      return acc;
    }, {} as Record<string, any>);

    const weeklyAnalytics = Object.values(weeklyData)
      .sort((a: any, b: any) => new Date(a.week).getTime() - new Date(b.week).getTime())
      .slice(-8) // Last 8 weeks
      .map((week: any) => ({
        week: new Date(week.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        activities: week.activities,
        'Duration (hrs)': Math.round(week.duration / 3600 * 10) / 10,
        'Calories': week.calories
      }));

    return {
      totalActivities,
      totalDuration: Math.round(totalDuration / 3600 * 10) / 10, // hours
      totalCalories,
      activityDistribution,
      weeklyAnalytics,
      avgDuration: Math.round(totalDuration / totalActivities / 60), // minutes
    };
  }, [fitnessData]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No Fitness Data</h3>
            <p className="text-gray-600">Start logging your fitness activities to see analytics here!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                <p className="text-2xl font-bold">{stats.totalActivities}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Time</p>
                <p className="text-2xl font-bold">{stats.totalDuration}h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Calories</p>
                <p className="text-2xl font-bold">{stats.totalCalories?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Duration</p>
                <p className="text-2xl font-bold">{stats.avgDuration}min</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Distribution</CardTitle>
            <CardDescription>Breakdown of your fitness activities</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.activityDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.activityDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Weekly Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Activity Trends</CardTitle>
            <CardDescription>Your fitness activity over the last 8 weeks</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.weeklyAnalytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="activities" fill="#8884d8" name="Activities" />
                <Bar dataKey="Duration (hrs)" fill="#82ca9d" name="Duration (hrs)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};